import React from 'react';
import { Chrome, Download, Monitor, Puzzle, LogIn, MousePointer, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const steps = [
  {
    icon: Download,
    title: 'Scarica l\'estensione',
    desc: 'Clicca il bottone qui sotto per scaricare il file ZIP dell\'estensione.',
    action: true,
  },
  {
    icon: Monitor,
    title: 'Estrai il file ZIP',
    desc: 'Apri il file scaricato e estrai il contenuto in una cartella sul tuo computer (es. Desktop/SchiliroCRM-Extension).',
  },
  {
    icon: Chrome,
    title: 'Apri Chrome Extensions',
    desc: 'Vai su chrome://extensions nel browser Chrome. Attiva la "Modalita sviluppatore" in alto a destra.',
  },
  {
    icon: Puzzle,
    title: 'Carica l\'estensione',
    desc: 'Clicca "Carica estensione non pacchettizzata" e seleziona la cartella dove hai estratto il ZIP.',
  },
  {
    icon: LogIn,
    title: 'Accedi al CRM',
    desc: 'Clicca l\'icona dell\'estensione nella barra di Chrome (icona blu "S"), poi inserisci le tue credenziali CRM.',
  },
  {
    icon: MousePointer,
    title: 'Naviga sui portali',
    desc: 'Vai su work.areabene.it (Bene Assicurazioni) o altri portali. Vedrai "CRM Connesso" in basso. Quando sei su una pagina di preventivo, clicca "Salva nel CRM".',
  },
];

export const EstensionePage = () => {
  return (
    <div className="p-5 md:p-7 min-h-screen" data-testid="estensione-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 text-white mb-4">
            <Chrome size={32} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Estensione Chrome</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Importa automaticamente i preventivi dai portali assicurativi direttamente nel CRM.
          </p>
        </div>

        {/* Download Button */}
        <div className="flex justify-center mb-8">
          <a href={`${API_URL}/api/static/schiliro-crm-extension.zip`} download>
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-base px-8 py-6 h-auto gap-3" data-testid="download-extension-btn">
              <Download size={20} />
              Scarica Estensione Chrome
            </Button>
          </a>
        </div>

        {/* Supported portals */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Portali Supportati</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { name: 'Bene Assicurazioni', url: 'areabene.it' },
              { name: 'Allianz', url: 'matrix.allianz.it' },
              { name: 'UnipolSai', url: 'essig.unipolsai.it' },
              { name: 'Prima.it', url: 'intermediari.prima.it' },
              { name: 'Nobis', url: 'nobis.it' },
              { name: 'Conte Intermediari', url: 'siaspa.com' },
              { name: 'Reale Mutua', url: 'grupporealemutua.it' },
              { name: 'ItalNext', url: 'grupporealemutua.it' },
              { name: 'Wakam', url: 'azurewebsites.net' },
              { name: 'National ASS', url: 'nationalautomotive.net' },
              { name: 'Genialpi+', url: 'genialpiu.it' },
              { name: 'Sol. Strade', url: 'attivatessere.it' },
              { name: 'HDI', url: 'hdia.it' },
            ].map(p => (
              <div key={p.name} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">{p.name}</p>
                  <p className="text-[11px] text-slate-400">{p.url}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Installation Steps */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-sm font-bold text-slate-900 mb-4">Come Installarla</h2>
          <div className="space-y-4">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                    <step.icon size={18} />
                  </div>
                  {i < steps.length - 1 && <div className="w-px h-full bg-slate-200 mt-1" />}
                </div>
                <div className="pb-4">
                  <p className="text-sm font-semibold text-slate-900">
                    <span className="text-blue-600 mr-1.5">{i + 1}.</span>{step.title}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{step.desc}</p>
                  {step.action && (
                    <a href={`${API_URL}/api/static/schiliro-crm-extension.zip`} download className="inline-block mt-2">
                      <Button size="sm" variant="outline" className="text-xs gap-1.5">
                        <Download size={13} /> Scarica ZIP
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5 mt-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Come funziona</p>
              <p className="text-sm text-emerald-700 mt-1 leading-relaxed">
                Una volta installata, l'estensione rileva quando sei su un portale assicurativo. 
                Quando trovi un preventivo, clicca "Salva nel CRM" e i dati (compagnia, premio, garanzie, targa) 
                vengono importati automaticamente nella tua sezione "I Miei Preventivi".
                Puoi poi modificare, assegnare ai colleghi o inviare al cliente via WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
